import type { Components, Theme } from '@mui/material/styles';

import { borderRadius } from './borderRadius';
import { spacing } from './spacing';

/**
 * Shared elevation for floating surfaces (popovers, menus, selects,
 * autocompletes, dialogs, tooltips). The base `MuiPaper` override below flattens
 * every Paper (`boxShadow: none`) so in-page cards/panels stay flat; this token
 * re-introduces a soft shadow *only* on the floating surfaces so they read as
 * lifted above the page. Slightly stronger in dark mode where a light shadow
 * would be invisible against the dark canvas.
 */
export const popupShadow = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? '0px 8px 28px rgba(0, 0, 0, 0.55)'
    : '0px 4px 25px rgba(0, 0, 0, 0.15)';

/**
 * Common theme component overrides
 * Uses theme callbacks to reference palette colors instead of hardcoded values
 */
export const getComponents = (): Components<Theme> => ({
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        borderRadius: borderRadius.md,
        backgroundColor: theme.palette.secondary.main,
        boxShadow: 'none',
        color: theme.palette.common.white,
        '&:disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
    },
    variants: [
      {
        props: { variant: 'primary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.common.black,
          },
        }),
      },
      {
        props: { variant: 'secondary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
        }),
      },
      {
        props: { variant: 'tertiary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.text.primary,
          borderRadius: borderRadius.md,
          '&:hover': {
            backgroundColor: theme.palette.grey[600],
          },
        }),
      },
      {
        // The AI accent. Anything that hands work to the model wears ocean
        // purple, so "the AI is about to do something" is legible at a glance
        // and never confused with an ordinary primary action.
        props: { variant: 'ai' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.ocean[700],
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.ocean[800],
          },
        }),
      },
      {
        props: { variant: 'link' },
        style: ({ theme }) => ({
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          textDecoration: 'underline',
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.dark,
            textDecoration: 'underline',
          },
          '&:disabled': {
            textDecoration: 'none',
          },
        }),
      },
    ],
  },

  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          backgroundColor: theme.palette.grey[200],
          borderRadius: borderRadius.md,
          fontSize: '0.875rem',
          paddingInlineEnd: '0.625rem',
        },
        '& .MuiInputBase-input': {
          borderRadius: '0rem',
          padding: '0.625rem 1rem',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
      }),
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
      }
    },
    variants: [
      {
        props: { variant: 'panel' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: borderRadius.md,
          padding: spacing.comfort,

        }),
      },
    ],
  },

  // ── Floating surfaces: re-add elevation the base MuiPaper override strips ──
  // The `&.MuiPaper-root` selector lifts specificity above the flat
  // `MuiPaper` root rule so the shadow actually wins. MuiPopover also covers
  // Menu / Select (they render their Paper through Popover), but MuiMenu is
  // listed too for belt-and-suspenders. Components that set their own shadow
  // via sx/PaperProps keep it — inline styles still win over these.
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        '&.MuiPaper-root': {
          boxShadow: popupShadow(theme),
        },
      }),
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        '&.MuiPaper-root': {
          boxShadow: popupShadow(theme),
        },
      }),
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: ({ theme }) => ({
        '&.MuiPaper-root': {
          boxShadow: popupShadow(theme),
        },
      }),
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        '&.MuiPaper-root': {
          boxShadow: popupShadow(theme),
        },
      }),
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        boxShadow: popupShadow(theme),
      }),
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        margin: '1rem',
        marginBottom: 0,
        borderRadius: '1rem',
        height: '3.25rem',
        width: 'calc(100% - 2rem)',
      }),
    },
  },

  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: 'Readex Pro',
        color: theme.palette.text.primary,
      }),
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.black[100]}`,
        color: theme.palette.text.secondary,
        borderRadius: borderRadius.md,
        '& .MuiAlert-message': {
          paddingRight: '8px',
        },
      }),
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: borderRadius.full,
        height: 6,
        '&.MuiLinearProgress-colorPrimary': {
          backgroundColor: theme.palette.grey[400],
        },
        '& .MuiLinearProgress-bar': {
          borderRadius: borderRadius.full,
          backgroundColor: theme.palette.blue[700],
        },
      }),
    },
  },
});
